import { useState, useEffect, createContext, useContext } from "react";
import { Outlet, NavLink, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Folder,
  FolderOpen,
  FolderPlus,
  MagnifyingGlass,
  Users,
  TelegramLogo,
  Export,
  Gear,
  CaretLeft,
  CaretRight,
  Plus,
  Globe,
  Bell,
  Question,
  Trash,
  PencilSimple,
  X,
  PuzzlePiece,
  CalendarBlank,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Context for sharing state
const AppContext = createContext();
export const useAppContext = () => useContext(AppContext);

const FOLDER_COLORS = [
  "#2196F3", "#4CAF50", "#F44336", "#FF9800", "#9C27B0", 
  "#00BCD4", "#E91E63", "#673AB7", "#3F51B5", "#009688"
];

const DolphinLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState({ total_profiles: 0, total_folders: 0 });
  const [folders, setFolders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(searchParams.get("folder") || null);
  
  // Folder dialog state
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderColor, setNewFolderColor] = useState("#2196F3");

  useEffect(() => {
    fetchStats();
    fetchFolders();
  }, []);

  useEffect(() => {
    const folder = searchParams.get("folder");
    setSelectedFolderId(folder);
  }, [searchParams]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/stats`);
      setStats(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchFolders = async () => {
    try {
      const res = await axios.get(`${API}/folders`);
      setFolders(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error("Введите название папки");
      return;
    }
    try {
      if (editingFolder) {
        await axios.put(`${API}/folders/${editingFolder.id}`, {
          name: newFolderName,
          color: newFolderColor,
        });
        toast.success("Папка обновлена");
      } else {
        await axios.post(`${API}/folders`, {
          name: newFolderName,
          color: newFolderColor,
        });
        toast.success("Папка создана");
      }
      fetchFolders();
      fetchStats();
      setFolderDialogOpen(false);
      setNewFolderName("");
      setNewFolderColor("#2196F3");
      setEditingFolder(null);
    } catch (e) {
      toast.error("Ошибка");
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!confirm("Удалить папку? Профили будут перемещены в 'Все профили'")) return;
    try {
      await axios.delete(`${API}/folders/${folderId}`);
      toast.success("Папка удалена");
      fetchFolders();
      fetchStats();
      if (selectedFolderId === folderId) {
        setSearchParams({});
      }
    } catch (e) {
      toast.error("Ошибка удаления");
    }
  };

  const openEditFolder = (folder) => {
    setEditingFolder(folder);
    setNewFolderName(folder.name);
    setNewFolderColor(folder.color);
    setFolderDialogOpen(true);
  };

  const selectFolder = (folderId) => {
    const isOnProfilesList = location.pathname === "/" || location.pathname === "/profiles";
    if (folderId) {
      if (isOnProfilesList) {
        setSearchParams({ folder: folderId });
      } else {
        navigate(`/?folder=${folderId}`);
      }
    } else {
      if (isOnProfilesList) {
        setSearchParams({});
      } else {
        navigate("/");
      }
    }
    setSelectedFolderId(folderId);
  };

  const contextValue = {
    refreshStats: fetchStats,
    refreshFolders: fetchFolders,
    folders,
    selectedFolderId,
    selectFolder,
  };

  return (
    <AppContext.Provider value={contextValue}>
      <div className="dolphin-layout">
        {/* Sidebar */}
        <aside className={`dolphin-sidebar ${collapsed ? "w-14" : ""}`} data-testid="sidebar">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-[#252A33]">
            {!collapsed && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center">
                  <Globe size={18} weight="bold" className="text-white" />
                </div>
                <span className="font-bold text-sm">AntiDetect</span>
              </div>
            )}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="icon-btn"
              data-testid="toggle-sidebar"
            >
              {collapsed ? <CaretRight size={16} /> : <CaretLeft size={16} />}
            </button>
          </div>

          {/* Search */}
          {!collapsed && (
            <div className="sidebar-header">
              <div className="sidebar-search">
                <MagnifyingGlass size={14} />
                <input
                  type="text"
                  placeholder="Найти папку"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="sidebar-search"
                />
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="sidebar-nav">
            {/* All profiles */}
            <div className="sidebar-section">
              <button
                onClick={() => selectFolder(null)}
                className={`folder-item w-full ${!selectedFolderId && location.pathname.includes("profile") ? "active" : ""}`}
                data-testid="nav-all-profiles"
              >
                <Users size={18} weight="duotone" />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">Все профили</span>
                    <span className="text-xs text-[#5C6370]">{stats.total_profiles}</span>
                  </>
                )}
              </button>
            </div>

            {/* Folders */}
            {!collapsed && (
              <div className="sidebar-section">
                <div className="flex items-center justify-between px-4 py-2">
                  <span className="text-[11px] font-semibold text-[#5C6370] uppercase tracking-wider">
                    Папки
                  </span>
                  <button
                    onClick={() => {
                      setEditingFolder(null);
                      setNewFolderName("");
                      setNewFolderColor("#2196F3");
                      setFolderDialogOpen(true);
                    }}
                    className="icon-btn w-6 h-6"
                    data-testid="create-folder-btn"
                  >
                    <FolderPlus size={14} />
                  </button>
                </div>
                {folders
                  .filter((f) => f.name.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map((folder) => (
                    <div
                      key={folder.id}
                      className={`folder-item group ${selectedFolderId === folder.id ? "active" : ""}`}
                      data-testid={`folder-${folder.id}`}
                    >
                      <button
                        onClick={() => selectFolder(folder.id)}
                        className="flex items-center gap-2 flex-1"
                      >
                        <Folder size={18} weight="duotone" style={{ color: folder.color }} />
                        <span className="flex-1 text-left truncate">{folder.name}</span>
                        <span className="text-xs text-[#5C6370]">{folder.profile_count || 0}</span>
                      </button>
                      <div className="hidden group-hover:flex items-center gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditFolder(folder);
                          }}
                          className="icon-btn w-6 h-6"
                        >
                          <PencilSimple size={12} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFolder(folder.id);
                          }}
                          className="icon-btn w-6 h-6 text-red-400"
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {/* Integrations */}
            <div className="sidebar-section">
              {!collapsed && <div className="sidebar-section-title">Интеграции</div>}
              <NavLink
                to="/telegram"
                className={({ isActive }) => `folder-item ${isActive ? "active" : ""}`}
                data-testid="nav-telegram"
              >
                <TelegramLogo size={18} weight="duotone" className="text-[#00BCD4]" />
                {!collapsed && <span>Telegram</span>}
              </NavLink>
              <NavLink
                to="/import-export"
                className={({ isActive }) => `folder-item ${isActive ? "active" : ""}`}
                data-testid="nav-import"
              >
                <Export size={18} weight="duotone" />
                {!collapsed && <span>Импорт / Экспорт</span>}
              </NavLink>
            </div>

            {/* Tools */}
            <div className="sidebar-section">
              {!collapsed && <div className="sidebar-section-title">Инструменты</div>}
              <NavLink
                to="/extensions"
                className={({ isActive }) => `folder-item ${isActive ? "active" : ""}`}
                data-testid="nav-extensions"
              >
                <PuzzlePiece size={18} weight="duotone" className="text-[#FF9800]" />
                {!collapsed && <span>Расширения</span>}
              </NavLink>
              <NavLink
                to="/schedule"
                className={({ isActive }) => `folder-item ${isActive ? "active" : ""}`}
                data-testid="nav-schedule"
              >
                <CalendarBlank size={18} weight="duotone" className="text-[#FF9800]" />
                {!collapsed && <span>Расписание</span>}
              </NavLink>
              <NavLink
                to="/settings"
                className={({ isActive }) => `folder-item ${isActive ? "active" : ""}`}
                data-testid="nav-settings"
              >
                <Gear size={18} weight="duotone" />
                {!collapsed && <span>Настройки</span>}
              </NavLink>
            </div>
          </nav>

          {/* Footer */}
          {!collapsed && (
            <div className="sidebar-footer">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#1A1D24] rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium">RU</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button className="icon-btn" data-testid="help-btn">
                  <Question size={18} />
                </button>
                <button className="icon-btn" data-testid="notifications-btn">
                  <Bell size={18} />
                </button>
              </div>
            </div>
          )}
        </aside>

        {/* Main content */}
        <main className="dolphin-main">
          <Outlet />
        </main>

        {/* Create/Edit Folder Dialog */}
        <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
          <DialogContent className="bg-[#1A1D24] border-[#252A33] max-w-md">
            <DialogHeader>
              <DialogTitle>{editingFolder ? "Редактировать папку" : "Новая папка"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-xs text-[#8A9099]">Название</Label>
                <Input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Название папки"
                  className="mt-1 bg-[#0D0F12] border-[#252A33]"
                  data-testid="folder-name-input"
                />
              </div>
              <div>
                <Label className="text-xs text-[#8A9099]">Цвет</Label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {FOLDER_COLORS.map((color) => (
                    <button
                      key={color}
                      onClick={() => setNewFolderColor(color)}
                      className={`w-8 h-8 rounded ${newFolderColor === color ? "ring-2 ring-white ring-offset-2 ring-offset-[#1A1D24]" : ""}`}
                      style={{ backgroundColor: color }}
                      data-testid={`color-${color}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <button
                onClick={() => setFolderDialogOpen(false)}
                className="action-btn action-btn-outline"
              >
                Отмена
              </button>
              <button
                onClick={handleCreateFolder}
                className="action-btn action-btn-primary"
                data-testid="save-folder-btn"
              >
                {editingFolder ? "Сохранить" : "Создать"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppContext.Provider>
  );
};

export default DolphinLayout;
